import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Ciclo lunar: algoritmo sinódico (idade desde a lua nova de 2000-01-06 18:14 UTC)
const SYNODIC = 29.53058867;
const KNOWN_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);

const PHASE_NAMES = [
  'Lua Nova', 'Lua Crescente', 'Quarto Crescente', 'Gibosa Crescente',
  'Lua Cheia', 'Gibosa Minguante', 'Quarto Minguante', 'Lua Balsâmica',
];

const PHASE_ADVICE: Record<string, string> = {
  'Lua Nova': 'Plante a intenção: mês novo, página branca. Escreva (só para si) o que quer que cresça.',
  'Lua Crescente': 'Atração em movimento: comece pequeno — a energia está em expansão.',
  'Quarto Crescente': 'Atrito que constrói: o obstáculo da semana é o teste da sua intenção.',
  'Gibosa Crescente': 'Ajuste e refina: quase lá — revise antes de entregar.',
  'Lua Cheia': 'Colheita e emoção à flor da pele: celebre, mas não assine nada em surto.',
  'Gibosa Minguante': 'Compartilhe e agradeça: o ciclo se completa ensinando.',
  'Quarto Minguante': 'Solte o que não rendeu: editar é parte de criar.',
  'Lua Balsâmica': 'Descanse: a balsâmica é o silêncio antes do próximo início.',
};

function computeMoon(now = new Date()) {
  const ageMs = now.getTime() - KNOWN_NEW_MOON;
  const age = ((ageMs / 86400000) % SYNODIC + SYNODIC) % SYNODIC;
  const idx = Math.floor((age / SYNODIC) * 8) % 8;
  const phase = PHASE_NAMES[idx];
  const illumination = Math.round(((1 - Math.cos((2 * Math.PI * age) / SYNODIC)) / 2) * 100);

  // Próximos eventos: próxima nova e próxima cheia
  const ageOfFull = SYNODIC / 2;
  const daysToFull = age < ageOfFull ? ageOfFull - age : SYNODIC - age + ageOfFull;
  const daysToNew = SYNODIC - age;
  const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });

  return {
    phase,
    illumination,
    advice: PHASE_ADVICE[phase] ?? '',
    nextFull: fmt(new Date(now.getTime() + daysToFull * 86400000)),
    nextNew: fmt(new Date(now.getTime() + daysToNew * 86400000)),
    daysToFull: Math.ceil(daysToFull),
    daysToNew: Math.ceil(daysToNew),
    age,
  };
}

export const LunarPage: React.FC = () => {
  const { user } = useAuth();
  const moon = computeMoon();
  const waxing = moon.age < SYNODIC / 2;

  return (
    <div className="min-h-[calc(100vh-4rem)] text-stela-fg">
      <main className="max-w-[760px] mx-auto px-4 py-8">
        <Link to="/" className="text-stela-muted text-sm hover:text-stela-lilac">← Voltar</Link>
        <h1 className="font-display font-semibold text-3xl mt-3">Ciclo Lunar</h1>
        <p className="text-stela-muted text-sm mt-2">
          Que fase da Lua você vive agora — e como navegar até a próxima lunação.
        </p>

        <div className="bg-stela-card border border-stela-line rounded-2xl p-6 mt-6">
          <div className="flex items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl text-stela-lilac">{moon.phase}</h2>
              <p className="text-stela-muted text-sm mt-1">{moon.illumination}% iluminada</p>
              <p className="text-stela-muted text-[13px] mt-3 max-w-[45ch]">{moon.advice}</p>
            </div>

            <div className="w-24 h-24 relative shrink-0" aria-hidden="true">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-stela-accent to-stela-accent2 opacity-20" />
              <div className="absolute inset-1.5 rounded-full bg-stela-ink" />
              <div
                className="absolute inset-1.5 rounded-full bg-gradient-to-r from-stela-accent to-stela-accent2"
                style={{ clipPath: `inset(0 ${100 - moon.illumination}% 0 0)` }}
              />
              <div
                className="absolute inset-1.5 rounded-full border border-stela-lilac/30"
                style={{
                  clipPath: waxing ? 'inset(0 0 0 50%)' : 'inset(0 50% 0 0)',
                  background: 'transparent',
                }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div className="bg-stela-card border border-stela-line rounded-2xl p-4">
            <b className="text-sm text-stela-lilac">☾ Próxima Cheia</b>
            <p className="text-stela-fg mt-1">{moon.nextFull}</p>
            <p className="text-stela-muted text-xs mt-0.5">em {moon.daysToFull} dias</p>
          </div>
          <div className="bg-stela-card border border-stela-line rounded-2xl p-4">
            <b className="text-sm text-stela-lilac">✦ Próxima Nova</b>
            <p className="text-stela-fg mt-1">{moon.nextNew}</p>
            <p className="text-stela-muted text-xs mt-0.5">em {moon.daysToNew} dias</p>
          </div>
        </div>

        <p className="text-stela-muted text-[11.5px] mt-4">
          Cálculo sinódico local (29,53 dias desde a lua nova de referência) — aproximado a um dia,
          sem efemérides externas.
        </p>

        <div className="bg-stela-card border border-stela-line rounded-2xl p-5 mt-6 text-center">
          <p className="text-stela-muted text-sm mb-3">
            Quer a leitura da luação pelo SEU mapa? A Stela cruza a fase com o seu Sol natal.
          </p>
          <Link
            to="/chat"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-stela-accent to-stela-accent2 text-white font-semibold text-sm px-6 py-2.5 rounded-full hover:brightness-110 transition"
          >
            Perguntar à Stela
          </Link>
          {!user && <span className="block text-stela-muted text-xs mt-2">conta grátis · 1 minuto</span>}
        </div>
      </main>
    </div>
  );
};